/**
 * Supabase Auth session handling (spec section 4: private application, deny by
 * default).
 *
 * The client here is used for authentication only. Data reads never go direct
 * to PostgREST from the browser -- they go through the Worker, which applies
 * the user-scoped client so RLS holds and so route-level authorization lives
 * in one place.
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createApiClient, type ApiClient } from './api';
import { readConfig, type AppConfig } from './config';

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; session: Session };

type AuthContextValue = {
  state: AuthState;
  config: AppConfig;
  api: ApiClient;
  supabase: SupabaseClient;
  signInWithPassword(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ config, children }: { config: AppConfig; children: ReactNode }) {
  const supabase = useMemo(
    () =>
      createClient(config.supabaseUrl, config.supabaseAnonKey, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      }),
    [config.supabaseUrl, config.supabaseAnonKey],
  );

  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setState(data.session ? { status: 'signed-in', session: data.session } : { status: 'signed-out' });
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      setState(session ? { status: 'signed-in', session } : { status: 'signed-out' });
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [supabase]);

  const api = useMemo(
    () =>
      createApiClient(config.apiBaseUrl, async () => {
        // Read the session at call time rather than closing over it: the
        // supabase client refreshes tokens in the background, and a stale
        // closure would send an expired JWT after an hour of idling.
        const { data } = await supabase.auth.getSession();
        return data.session?.access_token ?? null;
      }),
    [config.apiBaseUrl, supabase],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      config,
      api,
      supabase,
      async signInWithPassword(email, password) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
      },
      async signOut() {
        await supabase.auth.signOut();
      },
    }),
    [state, config, api, supabase],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}

export function useApi(): ApiClient {
  return useAuth().api;
}

export { readConfig };
